import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const SUMMARY_API = "http://127.0.0.1:8000/api/investigations/patient-summary/";

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: `1px solid ${Colors.border.input}`,
  outline: "none",
  fontSize: "14px",
  background: Colors.bg.paper,
  boxSizing: "border-box",
  color: Colors.text.body,
};

const thStyle = {
  textAlign: "left",
  padding: "14px",
  background: Colors.bg.hover,
  color: Colors.text.header,
  fontSize: "13px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontWeight: 800,
};

const tdStyle = {
  padding: "14px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontSize: "14px",
  color: Colors.text.body,
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export default function InvestigationPatientSummaryPage() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async (searchValue = "") => {
    try {
      setLoadingPatients(true);
      setErrorMsg("");

      const url = searchValue
        ? `${SUMMARY_API}?search=${encodeURIComponent(searchValue)}`
        : SUMMARY_API;

      const res = await axios.get(url);

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setPatients(data);
    } catch (error) {
      console.error("Summary fetch error:", error);

      if (error.response?.data) {
        setErrorMsg(
          typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data)
        );
      } else {
        setErrorMsg("Unable to load investigation patient summary.");
      }

      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPatients(search);
  };

  const clearSearch = () => {
    setSearch("");
    fetchPatients("");
  };

  const openHistoryPage = (patient) => {
    navigate(ROUTES.investigation_history_details(patient.patient_uhid), {
      state: { patient },
    });
  };

  return (
    <div style={{ minHeight: "100vh", background: Colors.bg.app, padding: "32px 18px" }}>
      <div
        style={{
          maxWidth: "1380px",
          margin: "0 auto",
          background: Colors.bg.paper,
          borderRadius: "24px",
          boxShadow: Colors.shadow.brand,
          overflow: "hidden",
          border: `1px solid ${Colors.border.light}`,
        }}
      >
        <div style={{ padding: "26px 30px 18px", borderBottom: `1px solid ${Colors.border.light}` }}>
          <h2 style={{ margin: 0, fontSize: "28px", fontWeight: 800, color: Colors.text.title }}>
            Investigation Patient Summary
          </h2>
        </div>

        <div style={{ padding: "28px" }}>
          {errorMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#fff4f4",
                border: `1px solid ${Colors.status.danger}`,
                color: Colors.status.danger,
                borderRadius: "12px",
              }}
            >
              {errorMsg}
            </div>
          )}

          <form
            onSubmit={handleSearch}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 140px 120px",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            <input
              type="text"
              placeholder="Search by patient UHID or patient name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={inputStyle}
            />

            <button
              type="submit"
              style={{
                border: "none",
                borderRadius: "12px",
                background: Colors.brand.primary,
                color: "#fff",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Search
            </button>

            <button
              type="button"
              onClick={clearSearch}
              style={{
                borderRadius: "12px",
                border: `1px solid ${Colors.border.input}`,
                background: Colors.bg.paper,
                color: Colors.text.body,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          </form>

          <div style={{ overflowX: "auto", border: `1px solid ${Colors.border.light}`, borderRadius: "16px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1080px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Patient UHID</th>
                  <th style={thStyle}>Patient Name</th>
                  <th style={thStyle}>Admission Number</th>
                  <th style={thStyle}>Doctor Name</th>
                  <th style={thStyle}>Total Investigations</th>
                  <th style={thStyle}>Last Investigation Date</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loadingPatients ? (
                  <tr>
                    <td colSpan="7" style={tdStyle}>
                      Loading patients...
                    </td>
                  </tr>
                ) : patients.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={tdStyle}>
                      No patient investigation history found.
                    </td>
                  </tr>
                ) : (
                  patients.map((item, index) => (
                    <tr key={`${item.patient_uhid}-${index}`}>
                      <td style={tdStyle}>{item.patient_uhid || "-"}</td>
                      <td style={tdStyle}>{item.patient_name || "-"}</td>
                      <td style={tdStyle}>{item.admission_number || "-"}</td>
                      <td style={tdStyle}>{item.doctor_name || "-"}</td>
                      <td style={tdStyle}>{item.total_investigations || 0}</td>
                      <td style={tdStyle}>{formatDateTime(item.last_investigation_at)}</td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => openHistoryPage(item)}
                          style={{
                            border: "none",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            background: Colors.brand.primary,
                            color: "#fff",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          View History
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}